import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Stat, Badge, EmptyState } from '../components/ui.js';
import { Icon } from '../components/Icon.js';

interface Subscription {
  subscription_id: string;
  status: string;
  credits_included: number;
  credits_used: number;
  credits_topup_balance: number;
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
}

interface Invoice {
  invoice_id?: string;
  amount_cents?: number;
  currency?: string;
  status?: string;
  created_at?: string;
}

const STATUS_KIND: Record<string, 'ok' | 'warn' | 'danger' | 'muted'> = {
  active: 'ok', trialing: 'warn', past_due: 'danger', cancelled: 'muted', paused: 'muted',
};

export function Billing() {
  const api = useApi();
  const { t, lang } = useLang();
  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<Subscription>('/billing/subscription'),
  });
  const { data: invoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get<{ items: Invoice[] }>('/billing/invoices'),
  });

  const used = sub?.credits_used ?? 0;
  const included = sub?.credits_included ?? 0;
  const remaining = Math.max(0, included - used);
  const pct = included > 0 ? Math.min(100, (used / included) * 100) : 0;
  const dateLocale = lang === 'ar' ? 'ar-EG' : 'en-US';

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.billing.title')} sub={t('page.billing.sub')} />

      {sub ? (
        <div className="card card--pad">
          <div className="between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>{t('page.billing.sub.h')}</div>
              <div className="flex aic gap12" style={{ flexWrap: 'wrap' }}>
                <Badge kind={STATUS_KIND[sub.status] ?? 'muted'} plain icon="checkcircle">
                  {t(`billing.status.${sub.status}`) || sub.status}
                </Badge>
                {sub.trial_ends_at && (
                  <Badge kind="warn" plain icon="clock">
                    {t('page.billing.trial.ends')} {new Date(sub.trial_ends_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' })}
                  </Badge>
                )}
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
                {t('page.billing.sub.period')}:{' '}
                <span className="mono" dir="ltr">
                  {new Date(sub.current_period_start).toLocaleDateString('en-GB')} → {new Date(sub.current_period_end).toLocaleDateString('en-GB')}
                </span>
              </div>
            </div>
            <div className="mono muted" style={{ fontSize: 11.5, textAlign: 'end' }} dir="ltr">
              ID: {sub.subscription_id.slice(-12)}
            </div>
          </div>

          <div className="pgrid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginTop: 22 }}>
            <Stat icon="bolt"    label={t('page.billing.stat.used')}      value={used.toLocaleString('en-US')} />
            <Stat icon="refresh" label={t('page.billing.stat.remaining')} value={remaining.toLocaleString('en-US')} />
            <Stat icon="plus"    label={t('page.billing.stat.topup')}     value={(sub.credits_topup_balance ?? 0).toLocaleString('en-US')} />
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="between" style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 600 }}>{t('page.billing.usage')}</div>
              <div className="mono" style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{pct.toFixed(0)}%</div>
            </div>
            <div style={{ height: 8, background: 'var(--bg-4)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: pct > 90 ? 'var(--danger)' : pct > 70 ? 'var(--warn)' : 'var(--accent)',
                transition: 'width .25s',
              }} />
            </div>
          </div>
        </div>
      ) : (
        <EmptyState icon="card" h={t('page.billing.empty.h')} p={t('page.billing.empty.p')} />
      )}

      <div>
        <h2 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 14px' }}>{t('page.billing.invoices.h')}</h2>
        {(invoices?.items?.length ?? 0) === 0 ? (
          <EmptyState icon="doc" h={t('page.billing.invoices.empty.h')} p={t('page.billing.invoices.empty.p')} />
        ) : (
          <div className="card tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('page.billing.invoices.col.id')}</th>
                  <th>{t('page.billing.invoices.col.amount')}</th>
                  <th>{t('page.billing.invoices.col.status')}</th>
                  <th>{t('page.billing.invoices.col.date')}</th>
                </tr>
              </thead>
              <tbody>
                {(invoices?.items ?? []).map((inv, i) => (
                  <tr key={inv.invoice_id ?? i}>
                    <td className="mono" dir="ltr">{inv.invoice_id ?? '—'}</td>
                    <td className="mono tbl__cell-strong">
                      {inv.amount_cents != null ? (inv.amount_cents / 100).toLocaleString() : '—'} {inv.currency ?? ''}
                    </td>
                    <td>
                      <Badge kind={inv.status === 'paid' ? 'ok' : inv.status === 'open' ? 'warn' : 'muted'} plain>
                        {inv.status ?? '—'}
                      </Badge>
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card--pad">
        <div className="flex aic gap8" style={{ marginBottom: 8 }}>
          <Icon name="shield" />
          <div style={{ fontSize: 14, fontWeight: 700 }}>{t('page.billing.payment.h')}</div>
        </div>
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.7 }}>{t('page.billing.payment.p')}</div>
      </div>
    </div>
  );
}
